import { z } from 'zod';
import { digestSchema, sealContent, verifyContent } from '../hashing/hash.js';
import { pinnedDefinitionSchema } from '../steps/definitions.js';
/**
 * What this person attested to, on this day, in these words.
 *
 * The question a surveyor asks is "what did this person agree to on 12 March", and it must
 * be answerable two years later without joining live documents. Definition edits are
 * legal — `presentation` is editable in place by design (ADR-006 §7) — so a snapshot made
 * of POINTERS would silently change what the person is recorded as having seen every time
 * someone fixes a typo in a heading. A snapshot therefore embeds BYTES.
 *
 * There is no update path. Not "an update path nobody calls" — no method exists to write
 * over a stored snapshot, and a later session writes sequence 2 beside sequence 1.
 */
export const snapshotStepSchema = z.object({
    instanceId: z.string().min(1),
    type: z.string().min(1),
    state: z.string().min(1),
    /** The pinned definition, embedded whole — canonical bytes, digest and presentation. */
    pinned: pinnedDefinitionSchema,
    /** Exactly as stored on the instance. Never re-validated here: it is a record, not input. */
    submission: z.unknown(),
    submittedAt: z.string().datetime().nullable(),
    /**
     * Digests of the artifacts sealed for this step, so the files can be proved unchanged.
     *
     * NOT every accepted upload against it, which is what this said and did until #588: the upload
     * a submission names, nothing for a step answered `notReady` or never submitted, and otherwise
     * every accepted one, which `hr-record` relies on because it names evidence per competency.
     */
    artifactDigests: z.array(digestSchema).default([]),
    /**
     * The sealed submission record this step's submission came from (ADR-013 §3, #208), bound by
     * its digest so the snapshot proves WHICH attempt it saw. Null for a step with no submission,
     * and for one submitted before records existed — retained staging still holds those, and
     * their snapshots must keep verifying, so a null reference is left out of the sealed bytes.
     */
    submissionRecord: z
        .object({ id: z.string().min(1), attempt: z.number().int().nonnegative(), contentHash: digestSchema })
        .nullable()
        .default(null),
});
export const packetSnapshotSchema = z.object({
    id: z.string().min(1),
    personId: z.string().min(1),
    docType: z.literal('packet-snapshot').default('packet-snapshot'),
    /** Which link produced it, and what that link was for. */
    tokenPurpose: z.string().min(1),
    /**
     * Monotonic per person. A second onboarding session writes 2 and leaves 1 untouched — the
     * mechanism that replaces an update path.
     */
    sequence: z.number().int().positive(),
    steps: z.array(snapshotStepSchema).min(1),
    capturedAt: z.string().datetime(),
    /** The exact bytes that were hashed, kept so the digest can be re-verified later. */
    canonicalContent: z.string().min(1),
    contentHash: digestSchema,
});
/** The projection that gets sealed. Deliberately excludes id and the seal itself. */
function sealable(input) {
    return {
        personId: input.personId,
        tokenPurpose: input.tokenPurpose,
        sequence: input.sequence,
        capturedAt: input.capturedAt,
        steps: input.steps.map((step) => ({
            instanceId: step.instanceId,
            type: step.type,
            state: step.state,
            pinned: {
                definitionId: step.pinned.definitionId,
                version: step.pinned.version,
                contentHash: step.pinned.contentHash,
                canonicalContent: step.pinned.canonicalContent,
                presentationSnapshot: step.pinned.presentationSnapshot,
                pinnedAt: step.pinned.pinnedAt,
            },
            submission: step.submission,
            submittedAt: step.submittedAt,
            artifactDigests: step.artifactDigests,
            // Only when present: a snapshot sealed before #208 has none, and adding a null here
            // would change its canonical bytes and fail every one of them on verification.
            ...(step.submissionRecord === null
                ? {}
                : { submissionRecord: { id: step.submissionRecord.id, attempt: step.submissionRecord.attempt, contentHash: step.submissionRecord.contentHash } }),
        })),
    };
}
/**
 * Build and seal. The caller supplies the sequence, because deciding it needs a read the
 * kernel must not perform.
 */
export async function buildPacketSnapshot(input) {
    const steps = input.instances.map((instance) => ({
        instanceId: instance.id,
        type: instance.type,
        state: instance.state,
        pinned: instance.pinned,
        submission: instance.submission,
        submittedAt: instance.submittedAt,
        artifactDigests: [...input.artifactDigestsFor(instance)],
        submissionRecord: input.submissionRecordFor?.(instance) ?? null,
    }));
    const projection = {
        personId: input.personId,
        tokenPurpose: input.tokenPurpose,
        sequence: input.sequence,
        steps,
        capturedAt: input.capturedAt,
    };
    const sealed = await sealContent(sealable(projection));
    return packetSnapshotSchema.parse({
        id: input.id,
        docType: 'packet-snapshot',
        ...projection,
        canonicalContent: sealed.canonical,
        contentHash: sealed.contentHash,
    });
}
/**
 * Does the stored snapshot still hash to what it claims?
 *
 * Two checks, not one. Verifying the stored canonical bytes against the stored digest
 * catches a corrupted digest but NOT a snapshot whose fields were edited and whose
 * canonical bytes were edited to match. Re-canonicalising the fields and comparing to the
 * stored bytes is what catches that, and it is the check that matters.
 */
export async function verifyPacketSnapshot(snapshot) {
    if (!(await verifyContent(snapshot.canonicalContent, snapshot.contentHash)))
        return false;
    const rebuilt = await sealContent(sealable(snapshot));
    return rebuilt.canonical === snapshot.canonicalContent && rebuilt.contentHash === snapshot.contentHash;
}
//# sourceMappingURL=snapshot.js.map